import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import type {
  TelemetryValue,
  GpsData,
  DtcEntry,
} from "../types/telemetry";
import type { CalibrationEntry } from "../types/config";
import { useMqttContext } from "../contexts/MqttContext";
import {
  STALE_TELEMETRY_MAX_AGE_MS,
  shouldExpireLiveTelemetryMetric,
} from "../lib/telemetryFreshness";

const TRAIL_MAX_LENGTH = 500;

/** Batching interval for metric state updates (ms) */
const METRICS_BATCH_MS = 100;

/**
 * Build a new TelemetryValue from a raw payload, carrying forward min/max/peak
 * from a previous entry when available.
 *
 * Exported for unit-testing the pure computation.
 */
export function updateTelemetryValue(
  prev: TelemetryValue | undefined,
  raw: string,
  numValue: number,
  now: number,
): TelemetryValue {
  return {
    raw,
    value: numValue,
    timestamp: now,
    min: isNaN(numValue)
      ? (prev?.min ?? NaN)
      : prev && !isNaN(prev.min)
        ? Math.min(prev.min, numValue)
        : numValue,
    max: isNaN(numValue)
      ? (prev?.max ?? NaN)
      : prev && !isNaN(prev.max)
        ? Math.max(prev.max, numValue)
        : numValue,
    peak: isNaN(numValue)
      ? (prev?.peak ?? NaN)
      : prev && !isNaN(prev.peak)
        ? Math.max(prev.peak, numValue)
        : numValue,
    peakTimestamp: isNaN(numValue)
      ? (prev?.peakTimestamp ?? now)
      : prev && !isNaN(prev.peak) && numValue <= prev.peak
        ? prev.peakTimestamp
        : now,
  };
}

/**
 * Parse a telemetry payload that may be JSON or legacy OBD text.
 *
 * JSON format:  `{"value": 3500, "ts": 1234567890}`
 * Legacy format: `"3500 revolutions_per_minute"`
 */
export function parsePayload(raw: string): { value: number; ts: number | null } {
  // Try JSON format first: {"value": X, "ts": T}
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null && "value" in parsed) {
      const obj = parsed as Record<string, unknown>;
      return {
        value:
          typeof obj.value === "number"
            ? obj.value
            : parseFloat(String(obj.value)),
        ts: typeof obj.ts === "number" ? obj.ts : null,
      };
    }
  } catch {
    // Not JSON, fall through to legacy parsing
  }
  // Legacy: "3500 revolutions_per_minute"
  const match = raw.match(/^-?[\d.]+/);
  return { value: match ? parseFloat(match[0]) : NaN, ts: null };
}

const emptyGps: GpsData = {
  latitude: null,
  longitude: null,
  altitude: null,
  speed: null,
  track: null,
  geohash: null,
  timestamp: 0,
};

/**
 * Central telemetry state hook.
 *
 * Subscribes to the shared MQTT context, parses incoming messages, and
 * exposes the latest values for metrics, GPS, and DTCs.
 *
 * Must be used inside an `<MqttProvider>`.
 */
export function useTelemetry(topicPrefix: string, calibration?: CalibrationEntry[]) {
  const [metrics, setMetrics] = useState<Map<string, TelemetryValue>>(
    () => new Map(),
  );
  // Keep a ref to the latest committed metrics so handleMessage can look up
  // previous values without depending on React state (avoids re-creating the callback).
  const metricsRef = useRef<Map<string, TelemetryValue>>(new Map());
  const [gps, setGps] = useState<GpsData>(emptyGps);
  const [trail, setTrail] = useState<[number, number][]>([]);
  const [dtcs, setDtcs] = useState<DtcEntry[]>([]);

  // Keep a ref for the trail so we can append without causing re-renders on every GPS update
  const trailRef = useRef<[number, number][]>([]);
  const lastPosTimestampRef = useRef<number>(0);
  const prefixRef = useRef(topicPrefix);
  prefixRef.current = topicPrefix;

  // Build a lookup map from calibration entries keyed by full topic
  const calibrationMap = useMemo(() => {
    const map = new Map<string, CalibrationEntry>();
    if (calibration) {
      for (const entry of calibration) {
        map.set(entry.topic, entry);
      }
    }
    return map;
  }, [calibration]);

  // Keep a ref so the message handler can access it without re-creating
  const calibrationMapRef = useRef(calibrationMap);
  calibrationMapRef.current = calibrationMap;

  // -- Batched metrics accumulation --
  const pendingMetricsRef = useRef<Map<string, TelemetryValue>>(new Map());
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const staleMetricTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const clearStaleMetricTimer = useCallback((key: string) => {
    const timer = staleMetricTimersRef.current.get(key);
    if (timer !== undefined) {
      clearTimeout(timer);
      staleMetricTimersRef.current.delete(key);
    }
  }, []);

  const scheduleStaleMetricRemoval = useCallback(
    (key: string, timestamp: number) => {
      if (!shouldExpireLiveTelemetryMetric(key)) return;
      clearStaleMetricTimer(key);
      const delay = Math.max(
        0,
        STALE_TELEMETRY_MAX_AGE_MS - (Date.now() - timestamp) + 1,
      );
      const timer = setTimeout(() => {
        staleMetricTimersRef.current.delete(key);
        setMetrics((prev) => {
          const current = prev.get(key);
          if (
            !current ||
            current.timestamp !== timestamp ||
            Date.now() - current.timestamp <= STALE_TELEMETRY_MAX_AGE_MS
          ) {
            return prev;
          }
          const next = new Map(prev);
          next.delete(key);
          metricsRef.current = next;
          return next;
        });
      }, delay);
      staleMetricTimersRef.current.set(key, timer);
    },
    [clearStaleMetricTimer],
  );

  const flushMetrics = useCallback(() => {
    batchTimerRef.current = null;
    const pending = pendingMetricsRef.current;
    if (pending.size === 0) return;
    // Swap to a fresh map before the setState closure reads it
    const batch = pending;
    pendingMetricsRef.current = new Map();
    setMetrics((prev) => {
      const next = new Map(prev);
      for (const [k, v] of batch) {
        next.set(k, v);
        scheduleStaleMetricRemoval(k, v.timestamp);
      }
      metricsRef.current = next;
      return next;
    });
  }, [scheduleStaleMetricRemoval]);

  // Cleanup the batch and stale-metric timers on unmount
  useEffect(() => {
    return () => {
      if (batchTimerRef.current !== null) {
        clearTimeout(batchTimerRef.current);
      }
      for (const timer of staleMetricTimersRef.current.values()) {
        clearTimeout(timer);
      }
      staleMetricTimersRef.current.clear();
    };
  }, []);

  const { status: connectionStatus, subscribe, publish } = useMqttContext();

  const handleMessage = useCallback(
    (topic: string, payload: string) => {
      const now = Date.now();
      const prefix = prefixRef.current;

      // GPS topics
      if (topic.startsWith(`${prefix}gps/`)) {
        const field = topic.replace(`${prefix}gps/`, "");
        setGps((prev) => {
          const next = { ...prev, timestamp: now };
          let changed = false;
          switch (field) {
            case "latitude": {
              if (now - lastPosTimestampRef.current < 5000) break;
              const v = parseFloat(payload);
              if (!isNaN(v) && v !== prev.latitude) { next.latitude = v; changed = true; }
              break;
            }
            case "longitude": {
              if (now - lastPosTimestampRef.current < 5000) break;
              const v = parseFloat(payload);
              if (!isNaN(v) && v !== prev.longitude) { next.longitude = v; changed = true; }
              break;
            }
            case "altitude": {
              const v = parseFloat(payload);
              if (!isNaN(v) && v !== prev.altitude) { next.altitude = v; changed = true; }
              break;
            }
            case "speed": {
              const v = parseFloat(payload);
              if (!isNaN(v) && v !== prev.speed) { next.speed = v; changed = true; }
              break;
            }
            case "track": {
              const v = parseFloat(payload);
              if (!isNaN(v) && v !== prev.track) { next.track = v; changed = true; }
              break;
            }
            case "geohash":
              if (payload !== prev.geohash) { next.geohash = payload; changed = true; }
              break;
            case "pos": {
              const [lat, lon] = payload.split(",").map(Number);
              if (!isNaN(lat) && !isNaN(lon)) {
                lastPosTimestampRef.current = now;
                if (lat !== prev.latitude || lon !== prev.longitude) {
                  next.latitude = lat;
                  next.longitude = lon;
                  changed = true;
                }
                const newTrail = [
                  ...trailRef.current,
                  [lat, lon] as [number, number],
                ];
                if (newTrail.length > TRAIL_MAX_LENGTH) {
                  newTrail.splice(0, newTrail.length - TRAIL_MAX_LENGTH);
                }
                trailRef.current = newTrail;
                setTrail([...trailRef.current]);
              }
              break;
            }
          }
          // Returning prev skips the re-render for consumers; the debounced
          // lat/lng path above used to fall through and re-render on every
          // message because timestamp always changed.
          return changed ? next : prev;
        });

        if (field === "speed" || field === "altitude" || field === "track") {
          const value = parseFloat(payload);
          if (!isNaN(value)) {
            const key = `gps/${field}`;
            let calibratedValue = value;
            const cal = calibrationMapRef.current.get(topic);
            if (cal) {
              calibratedValue = value * cal.scale + cal.offset;
              if (cal.clampMin !== undefined && calibratedValue < cal.clampMin) calibratedValue = cal.clampMin;
              if (cal.clampMax !== undefined && calibratedValue > cal.clampMax) calibratedValue = cal.clampMax;
            }
            const prev = pendingMetricsRef.current.get(key) ?? metricsRef.current.get(key);
            const tv = updateTelemetryValue(prev, payload, calibratedValue, now);
            pendingMetricsRef.current.set(key, tv);
            if (batchTimerRef.current === null) {
              batchTimerRef.current = setTimeout(flushMetrics, METRICS_BATCH_MS);
            }
          }
        }

        return;
      }

      // DTC topics
      if (topic.startsWith(`${prefix}DTC/`)) {
        const code = topic.replace(`${prefix}DTC/`, "");
        if (code === "_clear") {
          if (payload === "true") {
            setDtcs([]);
          }
          return;
        }
        if (payload === "") {
          setDtcs((prev) => prev.filter((d) => d.code !== code));
          return;
        }
        setDtcs((prev) => {
          // Replace existing DTC with same code, or add new
          const filtered = prev.filter((d) => d.code !== code);
          return [...filtered, { code, description: payload, timestamp: now }];
        });
        return;
      }

      // Skip radio topics — handled by useRadioTranscripts
      if (topic.startsWith(`${prefix}radio/`)) {
        return;
      }

      // Skip status request/response topics
      if (topic.startsWith(`${prefix}status/`)) {
        return;
      }

      // All other {prefix}* topics are metrics
      if (topic.startsWith(prefix)) {
        const key = topic.replace(prefix, "");
        const { value } = parsePayload(payload);
        // Apply calibration transform if configured for this topic
        let calibratedValue = value;
        const cal = calibrationMapRef.current.get(topic);
        if (cal) {
          calibratedValue = value * cal.scale + cal.offset;
          if (cal.clampMin !== undefined && calibratedValue < cal.clampMin) calibratedValue = cal.clampMin;
          if (cal.clampMax !== undefined && calibratedValue > cal.clampMax) calibratedValue = cal.clampMax;
        }
        const prev =
          pendingMetricsRef.current.get(key) ?? metricsRef.current.get(key);
        const tv = updateTelemetryValue(prev, payload, calibratedValue, now);
        pendingMetricsRef.current.set(key, tv);
        if (batchTimerRef.current === null) {
          batchTimerRef.current = setTimeout(flushMetrics, METRICS_BATCH_MS);
        }
      }
    },
    [flushMetrics],
  );

  useEffect(() => {
    const unsubscribe = subscribe(handleMessage);
    return unsubscribe;
  }, [subscribe, handleMessage]);

  return {
    metrics,
    gps,
    trail,
    dtcs,
    connectionStatus,
    publish,
  };
}
